import {
  Box,
  Checkbox,
  Chip,
  IconButton,
  SxProps,
  Tooltip,
  Typography,
  Badge,
} from "@mui/material";
import { Message, MoreVert } from "@mui/icons-material";
import { Dispatch } from "@reduxjs/toolkit";
import { ReactNode } from "react";
import { LockedIcon, UnlockedIcon } from "../Common/assets/Icons";
import { StickyStyle } from "../Common/styles/table";
import { featuresMap } from "../../utils/features";
import {
  appendAccessibleParticipants,
  appendParticipantIds,
} from "../../Redux/reducers/participantsSlice";
import StatusBadge from "../Common/UI/StatusTag";
import { getFrozenCellSx } from "../../hooks/useStickyColumnOffsets";
import { DateTime } from "luxon";
import {
  StudyFilterContext,
  renderStudyColumnHeader,
} from "../../utils/studyColumnFilters";

export type StudyParticipantColumnDef = {
  key: string;
  label: string;
  freezable: boolean;
  minWidth?: number;
  stickyLeftAlways?: boolean;
  stickyRight?: boolean;
  padding?: "checkbox" | "normal";
  align?: "left" | "center" | "right";
  renderHeader: () => ReactNode;
  renderCell: (row: any, checked: boolean) => ReactNode;
};

const statusMap: Record<string, { label: string; variant: string }> = {
  unverified: { label: "Unverified", variant: "pending" },
  included: { label: "included", variant: "active" },
  excluded: { label: "Excluded", variant: "completed" },
  withdrawn: { label: "Withdrawn", variant: "cancelled" },
  decide_later: { label: "Decide Later", variant: "sent" },
};

type BuildColumnsParams = {
  features: Record<string, boolean>;
  columns: Array<{
    label: string;
    varname: string;
    questionLabel?: string;
    questionId?: string;
    questionType?: string;
    questionChoices?: { label: string; id: string }[];
  }>;
  dispatch: Dispatch;
  filterContext?: StudyFilterContext;
  handleNavigate: (row: any) => void;
  handleShowTags: (tags: string[]) => void;
  handleActionClick: (row: any, action: string) => void;
  handleShowNoteDetails: (row: any) => void;
  handleMenuClick: (event: React.MouseEvent<HTMLElement>, row: any) => void;
  loading: boolean;
  dataEditable: boolean;
};

export function buildStudyParticipantColumns({
  features,
  columns,
  dispatch,
  handleNavigate,
  handleShowTags,
  handleActionClick,
  handleShowNoteDetails,
  handleMenuClick,
  loading,
  dataEditable,
  filterContext,
}: BuildColumnsParams): StudyParticipantColumnDef[] {
  const defs: StudyParticipantColumnDef[] = [
    {
      key: "select",
      label: "Select",
      freezable: false,
      stickyLeftAlways: true,
      padding: "checkbox",
      renderHeader: () => (
        <Checkbox
          checked={false}
          onChange={(event) => {
            if (event.target.checked) {
              dispatch(
                appendParticipantIds({
                  actionType: "multiple",
                  checked: event.target.checked,
                }),
              );
              dispatch(
                appendAccessibleParticipants({
                  actionType: "multiple",
                  checked: event.target.checked,
                }),
              );
            }
          }}
        />
      ),
      renderCell: (row, checked) => (
        <Checkbox
          checked={checked}
          onClick={(e) => e.stopPropagation()}
          onChange={(event) => {
            dispatch(
              appendParticipantIds({
                actionType: "single",
                rowId: row?.id,
                checked: event.target.checked,
              }),
            );
            dispatch(
              appendAccessibleParticipants({
                actionType: "single",
                rowId: row?.id,
                checked: event.target.checked,
                canEdit: row?.canAssignConsent === true,
              }),
            );
          }}
        />
      ),
    },
    {
      key: "participantId",
      label: "Participant ID",
      freezable: true,
      minWidth: 165,
      renderHeader: () =>
        renderStudyColumnHeader(
          "participantId",
          "Participant ID",
          filterContext,
        ),
      renderCell: (row) => (
        <Typography variant="body1" fontWeight="regular" sx={{ flexShrink: 0 }}>
          {row?.participantId || "-"}
        </Typography>
      ),
    },
    {
      key: "email",
      label: "Email",
      freezable: true,
      minWidth: 180,
      renderHeader: () => "Email",
      renderCell: (row) => row?.email || "-",
    },
    {
      key: "name",
      label: "Name",
      freezable: true,
      minWidth: 160,
      renderHeader: () => "Name",
      renderCell: (row) => {
        const full = [row?.firstName, row?.lastName].filter(Boolean).join(" ");
        return full || "-";
      },
    },
  ];

  if (features[featuresMap.participantGroups]) {
    defs.push({
      key: "groupCode",
      label: "Group Code",
      freezable: true,
      minWidth: 120,
      renderHeader: () =>
        renderStudyColumnHeader("groupCode", "Group Code", filterContext),
      renderCell: (row) => row?.groupCode,
    });
  }

  defs.push(
    {
      key: "site",
      label: "Site",
      freezable: true,
      minWidth: 180,
      renderHeader: () =>
        renderStudyColumnHeader("site", "Site", filterContext),
      renderCell: (row) => row?.site,
    },
    {
      key: "screenerCompleted",
      label: "Screener Completed",
      freezable: true,
      minWidth: 180,
      renderHeader: () =>
        renderStudyColumnHeader(
          "screenerCompleted",
          "Screener Completed",
          filterContext,
        ),
      renderCell: (row) => (row?.hasCompletedScreener ? "Yes" : "No"),
    },
    {
      key: "screenerCompletedDate",
      label: "Screener Completion Date",
      freezable: true,
      minWidth: 260,
      renderHeader: () =>
        renderStudyColumnHeader(
          "screenerCompletedDate",
          "Screener Completion Date",
          filterContext,
        ),
      renderCell: (row) =>
        row?.screenerCompletedAt
          ? DateTime.fromISO(row.screenerCompletedAt).toFormat(
              "MM/dd/yyyy hh:mm a"
            )
          : "-",
    },
    {
      key: "screenerReceived",
      label: "Screener Email Received",
      freezable: true,
      minWidth: 200,
      renderHeader: () =>
        renderStudyColumnHeader(
          "screenerReceived",
          "Screener Email Received",
          filterContext,
        ),
      renderCell: (row) => (row?.hasReceivedScreener ? "Yes" : "No"),
    },
  );

  if (features[featuresMap.participantGroups]) {
    defs.push({
      key: "userGroup",
      label: "User Group",
      freezable: true,
      minWidth: 180,
      renderHeader: () =>
        renderStudyColumnHeader("userGroup", "User Group", filterContext),
      renderCell: (row) => row?.cohort,
    });
  }

  defs.push({
    key: "tags",
    label: "Tags",
    freezable: true,
    minWidth: 130,
    renderHeader: () =>
      renderStudyColumnHeader("tags", "Tags", filterContext),
    renderCell: (row) =>
      row?.tags && row.tags.length > 0 ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          <Chip
            sx={{
              backgroundColor: "secondary.main",
              paddingX: 0.2,
              paddingY: 0.25,
              borderRadius: 1.5,
              fontSize: 10,
              fontWeight: 600,
              color: "primary.main",
              height: 20,
            }}
            label={row.tags[0]}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          />
          {row.tags.length > 1 && (
            <Typography
              color="primary"
              fontSize="10px"
              fontWeight="regular"
              sx={{
                cursor: "pointer",
                textDecoration: "underline",
                minWidth: "6ch",
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleShowTags(row.tags);
              }}
            >
              +{row.tags.length - 1} more
            </Typography>
          )}
        </Box>
      ) : null,
  });

  if (features[featuresMap.randomization]) {
    defs.push({
      key: "randomization",
      label: "Randomization Group",
      freezable: true,
      minWidth: 200,
      renderHeader: () =>
        renderStudyColumnHeader(
          "randomization",
          "Randomization Group",
          filterContext,
        ),
      renderCell: (row) => row?.randomization,
    });
  }

  columns?.forEach((item) => {
    const columnKey = `custom:${item.questionId ?? item.varname}`;
    defs.push({
      key: columnKey,
      label: item.label,
      freezable: true,
      minWidth: 220,
      renderHeader: () =>
        renderStudyColumnHeader(columnKey, item.label, filterContext, {
          customColumn: {
            label: item.label,
            varname: item.varname,
            questionLabel: item.questionLabel,
            questionType: item.questionType || "text",
            questionChoices: item.questionChoices,
          },
        }),
      renderCell: (row) => row?.[item.varname]?.value,
    });
  });

  defs.push(
    {
      key: "createdAt",
      label: "Created On",
      freezable: true,
      minWidth: 180,
      renderHeader: () =>
        renderStudyColumnHeader("createdAt", "Created On", filterContext),
      renderCell: (row) => row?.createdOn,
    },
    {
      key: "status",
      label: "Status",
      freezable: true,
      minWidth: 130,
      renderHeader: () =>
        renderStudyColumnHeader("status", "Status", filterContext),
      renderCell: (row) => (
        <StatusBadge
          variant={statusMap[row?.status]?.variant as any}
          label={statusMap[row?.status]?.label ?? row?.status}
        />
      ),
    },
    {
      key: "lock",
      label: "Lock",
      freezable: false,
      align: "center",
      minWidth: 100,
      renderHeader: () =>
        renderStudyColumnHeader("lock", "Lock", filterContext),
      renderCell: (row) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <IconButton
            onClick={() => handleActionClick(row, "lockUnlock")}
            disabled={
              loading || row?.isArchived || !row?.canLock || !dataEditable
            }
          >
            {row?.isLocked ? <LockedIcon /> : <UnlockedIcon />}
          </IconButton>
        </Box>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      freezable: false,
      stickyRight: true,
      align: "right",
      minWidth: 100,
      renderHeader: () =>
        renderStudyColumnHeader("actions", "Actions", filterContext),
      renderCell: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {row?.notesCount > 0 ? (
            <Tooltip
              title={`${row.notesCount} note${
                row.notesCount !== 1 ? "s" : ""
              }`}
            >
              <IconButton onClick={() => handleShowNoteDetails(row)}>
                <Badge badgeContent={row.notesCount} color="primary">
                  <Message />
                </Badge>
              </IconButton>
            </Tooltip>
          ) : (
            <Box sx={{ width: "36px" }} />
          )}
          <IconButton onClick={(e) => handleMenuClick(e, row)}>
            <MoreVert />
          </IconButton>
        </Box>
      ),
    },
  );

  return defs;
}

export function getStudyColumnCellSx(
  column: StudyParticipantColumnDef,
  frozenKeys: string[],
  frozenKeysInOrder: string[],
  stickyLeft: Record<string, number>,
  isHeader: boolean,
): SxProps {
  const base: SxProps = column.minWidth ? { minWidth: column.minWidth } : {};

  if (column.stickyRight) {
    return {
      ...base,
      position: "sticky",
      right: 0,
      zIndex: isHeader ? 112 : 101,
      backgroundColor: isHeader ? "#F3F4F6" : "#ffffff",
      ...(isHeader ? { top: 0 } : {}),
    };
  }

  if (column.stickyLeftAlways) {
    return {
      ...base,
      ...StickyStyle,
      left: 0,
      zIndex: isHeader ? 111 : 101,
    };
  }

  if (frozenKeys.includes(column.key)) {
    return {
      ...base,
      ...StickyStyle,
      ...getFrozenCellSx(column.key, frozenKeysInOrder, stickyLeft, isHeader),
    };
  }

  return base;
}

export function getStudyColumnCellClassName(
  column: StudyParticipantColumnDef,
  frozenKeys: string[],
  lastLeftPinnedKey: string | null,
): string | undefined {
  if (column.stickyRight) {
    return "fixed-col right";
  }
  if (column.stickyLeftAlways || frozenKeys.includes(column.key)) {
    const classes = ["fixed-col", "left"];
    if (column.key === lastLeftPinnedKey) {
      classes.push("fixed-col-left-edge");
    }
    return classes.join(" ");
  }
  return undefined;
}
